import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { ArrowLeft, Users, TrendingUp, Building2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, PieChart, Pie, Cell } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ManpowerPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [manpowerData, setManpowerData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    if (selectedProject) {
      fetchManpowerData(selectedProject);
    }
  }, [selectedProject]);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
      if (response.data.length > 0) {
        setSelectedProject(response.data[0].project_id);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const fetchManpowerData = async (projectId) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/projects/${projectId}/manpower`);
      setManpowerData(response.data);
    } catch (error) {
      console.error('Error fetching manpower data:', error);
    } finally {
      setLoading(false);
    }
  };

  const COLORS = ['#3b82f6', '#22c55e', '#f97316'];

  // Prepare comparison data
  const comparisonData = manpowerData ? [
    { name: 'Current', tekfen_direct: manpowerData.current.tekfen_direct, tekfen_indirect: manpowerData.current.tekfen_indirect, subcontractor: manpowerData.current.subcontractor },
    { name: 'Planned', tekfen_direct: manpowerData.planned.tekfen_direct, tekfen_indirect: manpowerData.planned.tekfen_indirect, subcontractor: manpowerData.planned.subcontractor },
    { name: 'Peak', tekfen_direct: manpowerData.peak.tekfen_direct, tekfen_indirect: manpowerData.peak.tekfen_indirect, subcontractor: manpowerData.peak.subcontractor }
  ] : [];

  // Prepare pie chart data for current distribution
  const pieData = manpowerData ? [
    { name: 'Tekfen Direct', value: manpowerData.current.tekfen_direct, color: COLORS[0] },
    { name: 'Tekfen Indirect', value: manpowerData.current.tekfen_indirect, color: COLORS[1] },
    { name: 'Subcontractor', value: manpowerData.current.subcontractor, color: COLORS[2] }
  ] : [];

  if (loading && !manpowerData) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="w-12 h-12 border-3 border-gray-200 border-t-amber-500 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="pb-20 bg-gray-50 min-h-screen" data-testid="manpower-page">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-4 sticky top-0 z-10">
        <div className="flex items-center gap-3 mb-3">
          <button onClick={() => navigate('/')} className="p-1">
            <ArrowLeft size={20} className="text-gray-600" />
          </button>
          <div>
            <h1 className="text-lg font-bold text-gray-900">{t('manpower.title')}</h1>
            <p className="text-xs text-gray-500">{t('manpower.subtitle')}</p>
          </div>
        </div>
        
        <Select value={selectedProject} onValueChange={setSelectedProject}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder={t('common.selectProject')} />
          </SelectTrigger>
          <SelectContent>
            {projects.map((project) => (
              <SelectItem key={project.project_id} value={project.project_id}>
                {project.project_code} - {project.project_name.substring(0, 25)}...
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="px-4 py-4 space-y-4">
        {/* Current Manpower Summary */}
        {manpowerData && (
          <Card className="bg-gradient-to-r from-amber-500 to-orange-500 text-white">
            <CardContent className="p-4">
              <h3 className="text-sm font-medium opacity-80 mb-3">{t('manpower.currentWorkforce')}</h3>
              <div className="flex items-center justify-between">
                <div className="text-center">
                  <Users size={32} className="mx-auto mb-1 opacity-80" />
                  <p className="text-3xl font-bold">{manpowerData.current.total}</p>
                  <p className="text-xs opacity-70">{t('manpower.totalPersonnel')}</p>
                </div>
                <div className="text-right space-y-1">
                  <div className="flex items-center justify-end gap-2">
                    <span className="text-xs opacity-70">{t('manpower.vsPlanned')}:</span>
                    <span className={`text-sm font-bold ${manpowerData.current.total >= manpowerData.planned.total ? 'text-green-200' : 'text-red-200'}`}>
                      {((manpowerData.current.total / manpowerData.planned.total) * 100).toFixed(0)}%
                    </span>
                  </div>
                  <div className="flex items-center justify-end gap-2">
                    <span className="text-xs opacity-70">{t('manpower.vsPeak')}:</span>
                    <span className="text-sm font-bold text-white/80">
                      {((manpowerData.current.total / manpowerData.peak.total) * 100).toFixed(0)}%
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Current Distribution Pie Chart */}
        {manpowerData && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">{t('manpower.currentDistribution')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center">
                <div className="h-40 flex-1">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={pieData}
                        cx="50%"
                        cy="50%"
                        innerRadius={35}
                        outerRadius={60}
                        paddingAngle={3}
                        dataKey="value"
                      >
                        {pieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="space-y-2">
                  {pieData.map((item, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded" style={{ backgroundColor: item.color }} />
                      <div>
                        <p className="text-xs font-medium text-gray-700">{item.name}</p>
                        <p className="text-sm font-bold">{item.value}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Comparison Bar Chart */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">{t('manpower.comparison')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={comparisonData}>
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: '10px' }} />
                  <Bar dataKey="tekfen_direct" name="Tekfen Direct" fill="#3b82f6" stackId="a" />
                  <Bar dataKey="tekfen_indirect" name="Tekfen Indirect" fill="#22c55e" stackId="a" />
                  <Bar dataKey="subcontractor" name="Subcontractor" fill="#f97316" stackId="a" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Detailed Cards */}
        <div className="grid grid-cols-3 gap-3">
          <Card>
            <CardContent className="p-3">
              <p className="text-xs text-gray-500 mb-1">{t('manpower.current')}</p>
              <p className="text-xl font-bold text-gray-900">{manpowerData?.current.total}</p>
              <div className="mt-2 space-y-1 text-xs">
                <div className="flex justify-between">
                  <span className="text-gray-500">{t('manpower.direct')}</span>
                  <span className="font-medium">{manpowerData?.current.tekfen_direct}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">{t('manpower.indirect')}</span>
                  <span className="font-medium">{manpowerData?.current.tekfen_indirect}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">{t('manpower.subcontractor')}</span>
                  <span className="font-medium">{manpowerData?.current.subcontractor}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-3">
              <p className="text-xs text-gray-500 mb-1">{t('common.planned')}</p>
              <p className="text-xl font-bold text-blue-600">{manpowerData?.planned.total}</p>
              <div className="mt-2 space-y-1 text-xs">
                <div className="flex justify-between">
                  <span className="text-gray-500">{t('manpower.direct')}</span>
                  <span className="font-medium">{manpowerData?.planned.tekfen_direct}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">{t('manpower.indirect')}</span>
                  <span className="font-medium">{manpowerData?.planned.tekfen_indirect}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">{t('manpower.subcontractor')}</span>
                  <span className="font-medium">{manpowerData?.planned.subcontractor}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-3">
              <p className="text-xs text-gray-500 mb-1">{t('manpower.peak')}</p>
              <p className="text-xl font-bold text-amber-600">{manpowerData?.peak.total}</p>
              <div className="mt-2 space-y-1 text-xs">
                <div className="flex justify-between">
                  <span className="text-gray-500">{t('manpower.direct')}</span>
                  <span className="font-medium">{manpowerData?.peak.tekfen_direct}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">{t('manpower.indirect')}</span>
                  <span className="font-medium">{manpowerData?.peak.tekfen_indirect}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">{t('manpower.subcontractor')}</span>
                  <span className="font-medium">{manpowerData?.peak.subcontractor}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Utilization Metrics */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">{t('manpower.resourceUtilization')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <div className="flex justify-between text-xs mb-1">
                <span className="text-gray-600">{t('manpower.currentVsPlanned')}</span>
                <span className="font-medium">{((manpowerData?.current.total / manpowerData?.planned.total) * 100).toFixed(0)}%</span>
              </div>
              <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-blue-500 rounded-full"
                  style={{ width: `${Math.min((manpowerData?.current.total / manpowerData?.planned.total) * 100, 100)}%` }}
                />
              </div>
            </div>
            
            <div>
              <div className="flex justify-between text-xs mb-1">
                <span className="text-gray-600">{t('manpower.tekfenVsSubcon')}</span>
                <span className="font-medium">
                  {(((manpowerData?.current.tekfen_direct + manpowerData?.current.tekfen_indirect) / manpowerData?.current.total) * 100).toFixed(0)}% / {((manpowerData?.current.subcontractor / manpowerData?.current.total) * 100).toFixed(0)}%
                </span>
              </div>
              <div className="w-full h-2 bg-orange-200 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-blue-500 rounded-full"
                  style={{ width: `${((manpowerData?.current.tekfen_direct + manpowerData?.current.tekfen_indirect) / manpowerData?.current.total) * 100}%` }}
                />
              </div>
            </div>

            <div>
              <div className="flex justify-between text-xs mb-1">
                <span className="text-gray-600">{t('manpower.currentVsPeak')}</span>
                <span className="font-medium">{((manpowerData?.current.total / manpowerData?.peak.total) * 100).toFixed(0)}%</span>
              </div>
              <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-amber-500 rounded-full"
                  style={{ width: `${(manpowerData?.current.total / manpowerData?.peak.total) * 100}%` }}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ManpowerPage;
